import { Button } from '@components'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaChevronUp, FaPlus, FaTimes } from 'react-icons/fa'
import styled, { css } from 'styled-components'

type Props = {
  icon: React.ReactNode
  collapsedTitle: string
  expandedTitle: string
  subtitle: string
  amount?: string
  expanded: boolean
  canEdit?: boolean
  onAdd?: () => void
  onRemove?: () => void
  showRemove?: boolean
  addPending?: boolean
  children?: React.ReactNode
}

export const TripSection: React.FC<Props> = ({
  icon,
  collapsedTitle,
  expandedTitle,
  subtitle,
  amount,
  expanded,
  canEdit = false,
  onAdd,
  onRemove,
  showRemove = false,
  addPending = false,
  children
}) => {
  const [t] = useTranslation()
  const [folded, setFolded] = useState(false)

  useEffect(() => {
    if (!expanded) setFolded(false)
  }, [expanded])

  if (!expanded) {
    return (
      <CollapsedCard>
        <IconTile>{icon}</IconTile>
        <CollapsedText>
          <CollapsedTitle>{collapsedTitle}</CollapsedTitle>
          <CollapsedSubtitle>{subtitle}</CollapsedSubtitle>
        </CollapsedText>
        {canEdit && onAdd ? (
          <Button
            type="button"
            size="sm"
            isSecondary
            intent="primary"
            icon={<FaPlus />}
            showSpinner={addPending}
            onClick={onAdd}
          >
            {t('travelAndExpenses.trip.sectionAdd')}
          </Button>
        ) : null}
      </CollapsedCard>
    )
  }

  return (
    <ExpandedCard>
      <ExpandedHeader>
        <HeaderToggle
          type="button"
          aria-expanded={!folded}
          onClick={() => setFolded(current => !current)}
        >
          <IconTile $active>{icon}</IconTile>
          <ExpandedTitle>{expandedTitle}</ExpandedTitle>
        </HeaderToggle>
        <HeaderRight>
          {amount ? <SectionAmount>{amount}</SectionAmount> : null}
          <RemoveButton
            type="button"
            aria-label={
              folded
                ? t('travelAndExpenses.trip.sectionExpand')
                : t('travelAndExpenses.trip.sectionCollapse')
            }
            onClick={() => setFolded(current => !current)}
          >
            {folded ? <FaChevronDown /> : <FaChevronUp />}
          </RemoveButton>
          {showRemove && onRemove ? (
            <RemoveButton
              type="button"
              aria-label={t('travelAndExpenses.trip.sectionRemove')}
              onClick={onRemove}
            >
              <FaTimes />
            </RemoveButton>
          ) : null}
        </HeaderRight>
      </ExpandedHeader>
      {folded ? null : <SectionBody>{children}</SectionBody>}
    </ExpandedCard>
  )
}

const cardBase = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const CollapsedCard = styled.div`
  ${cardBase}
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const ExpandedCard = styled.section`
  ${cardBase}
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  box-shadow: ${({ theme }) => theme.shadow.soft};
`

const IconTile = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ theme }) => theme.iconSize.lg}rem;
  height: ${({ theme }) => theme.iconSize.lg}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme, $active }) =>
    $active ? `${theme.colors.primary}14` : theme.colors.neutralGray};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.iconGray)};

  svg {
    width: ${({ theme }) => theme.iconSize.sm}rem;
    height: ${({ theme }) => theme.iconSize.sm}rem;
  }
`

const CollapsedText = styled.div`
  flex: 1;
  min-width: 0;
`

const CollapsedTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const CollapsedSubtitle = styled.div`
  margin-top: 2px;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const ExpandedHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const HeaderToggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-shrink: 0;
`

const ExpandedTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const SectionAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.iconSize.md}rem;
  height: ${({ theme }) => theme.iconSize.md}rem;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.iconGray};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.neutralBlack};
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  svg {
    width: ${({ theme }) => theme.spacing.px12}rem;
    height: ${({ theme }) => theme.spacing.px12}rem;
  }
`

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`
